const SECTION_ID = 'cart-drawer';

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-panel]');
    this.overlay = this.querySelector('[data-overlay]');
    this.status = this.querySelector('[data-cart-status]');
    this.debounceTimers = new Map();
    this.lastActiveElement = null;

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.onKeydown = this.onKeydown.bind(this);
    this.animateShippingBar();

    if (!CartDrawer.globalBound) {
      CartDrawer.globalBound = true;
      document.addEventListener('click', CartDrawer.handleTriggerClick);
      document.addEventListener('submit', CartDrawer.handleProductFormSubmit);
    }
    CartDrawer.instance = this;
  }

  static handleTriggerClick(event) {
    const trigger = event.target.closest('[data-cart-drawer-open]');
    if (!trigger) return;
    event.preventDefault();
    CartDrawer.instance?.open(trigger);
  }

  static async handleProductFormSubmit(event) {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    const submitter = event.submitter;
    if (submitter && submitter.hasAttribute('data-no-drawer')) return;

    event.preventDefault();
    const button = submitter || form.querySelector('[type="submit"]');
    const originalText = button?.textContent;

    try {
      if (button) {
        button.disabled = true;
        button.dataset.loading = 'true';
      }

      const formData = new FormData(form);
      formData.append('sections', SECTION_ID);
      formData.append('sections_url', window.location.pathname);

      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        const msg = data.description || data.message || 'Could not add to cart';
        CartDrawer.instance?.announce(msg);
        if (button) {
          button.textContent = msg;
          setTimeout(() => {
            if (originalText) button.textContent = originalText;
          }, 2000);
        }
        return;
      }

      if (data.sections && data.sections[SECTION_ID]) {
        CartDrawer.instance?.replaceFromSection(data.sections[SECTION_ID]);
      } else {
        await CartDrawer.instance?.refresh();
      }
      CartDrawer.instance?.open(submitter);
      CartDrawer.broadcastUpdate();
    } catch (err) {
      console.error(err);
      CartDrawer.instance?.announce('Could not add to cart. Please try again.');
    } finally {
      if (button) {
        button.disabled = false;
        delete button.dataset.loading;
      }
    }
  }

  static broadcastUpdate() {
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
      .then((r) => r.json())
      .then((cart) => {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      })
      .catch(() => {});
  }

  open(returnFocusTo) {
    this.lastActiveElement = returnFocusTo || document.activeElement;
    this.classList.remove('hidden');
    this.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      this.panel.classList.remove('translate-x-full');
      this.overlay.classList.remove('opacity-0');
      this.overlay.classList.add('opacity-100');
    });
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onKeydown);
    setTimeout(() => this.querySelector('[data-close]')?.focus(), 300);
  }

  animateShippingBar(fromPercent = 0) {
    const bar = this.querySelector('[data-shipping-progress]');
    if (!bar) return;
    const target = Math.max(0, Math.min(100, parseFloat(bar.dataset.progress) || 0));
    const start = Math.max(0, Math.min(100, fromPercent));
    bar.style.width = `${start}%`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        bar.style.width = `${target}%`;
      });
    });
  }

  getShippingProgress() {
    const bar = this.querySelector('[data-shipping-progress]');
    if (!bar) return 0;
    const computed = parseFloat(getComputedStyle(bar).width);
    const track = bar.parentElement ? parseFloat(getComputedStyle(bar.parentElement).width) : 0;
    if (!track) return parseFloat(bar.style.width) || 0;
    return (computed / track) * 100;
  }

  close() {
    this.panel.classList.add('translate-x-full');
    this.overlay.classList.remove('opacity-100');
    this.overlay.classList.add('opacity-0');
    this.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', this.onKeydown);
    setTimeout(() => {
      this.classList.add('hidden');
      document.body.style.overflow = '';
      this.lastActiveElement?.focus?.();
    }, 300);
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  onClick(event) {
    if (event.target.closest('[data-close]') || event.target === this.overlay) {
      event.preventDefault();
      this.close();
      return;
    }
    const remove = event.target.closest('[data-remove]');
    if (remove) {
      event.preventDefault();
      const line = remove.closest('[data-cart-item]');
      if (line) this.updateQuantity(line.dataset.itemKey, 0);
    }
  }

  onChange(event) {
    const input = event.target.closest('[data-qty-input]');
    if (!input) return;
    const key = input.dataset.itemKey;
    const value = Math.max(0, parseInt(input.value || '0', 10));
    input.value = String(value);

    clearTimeout(this.debounceTimers.get(key));
    this.debounceTimers.set(
      key,
      setTimeout(() => this.updateQuantity(key, value), 300)
    );
  }

  async updateQuantity(key, quantity) {
    const line = this.querySelector(`[data-item-key="${CSS.escape(key)}"]`);
    if (line) this.setLineLoading(line, true);
    this.announce('Updating cart');

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id: key, quantity, sections: SECTION_ID }),
      });

      if (!res.ok) throw new Error('Cart update failed');
      const data = await res.json();

      if (data.sections && data.sections[SECTION_ID]) {
        this.replaceFromSection(data.sections[SECTION_ID]);
      } else {
        await this.refresh();
      }
      this.announce('Cart updated');
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
    } catch (err) {
      console.error(err);
      if (line) this.setLineLoading(line, false);
      this.announce('Could not update cart.');
    }
  }

  async refresh() {
    try {
      const res = await fetch(`${window.location.pathname}?sections=${SECTION_ID}`);
      const data = await res.json();
      if (data[SECTION_ID]) this.replaceFromSection(data[SECTION_ID]);
    } catch (err) {
      console.error(err);
    }
  }

  replaceFromSection(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('cart-drawer');
    if (!fresh) return;

    const prevProgress = this.getShippingProgress();

    this.innerHTML = fresh.innerHTML;
    this.panel = this.querySelector('[data-panel]');
    this.overlay = this.querySelector('[data-overlay]');
    this.status = this.querySelector('[data-cart-status]');

    if (this.getAttribute('aria-hidden') === 'false') {
      this.panel.classList.remove('translate-x-full');
      this.overlay.classList.remove('opacity-0');
      this.overlay.classList.add('opacity-100');
    }
    this.animateShippingBar(prevProgress);
  }

  setLineLoading(line, isLoading) {
    const loader = line.querySelector('[data-item-loading]');
    if (loader) loader.hidden = !isLoading;
    line.style.opacity = isLoading ? '0.6' : '';
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }
}

customElements.define('cart-drawer', CartDrawer);

document.addEventListener('cart:updated', (event) => {
  const cart = event.detail?.cart;
  if (!cart) return;
  document.querySelectorAll('[data-cart-count-bubble]').forEach((el) => {
    el.textContent = cart.item_count;
    el.hidden = cart.item_count === 0;
  });
});
